import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronRight, ChevronLeft, Store, Gift, Code, CreditCard, Eye } from 'lucide-react'

interface DemoTutorialProps {
  isVisible?: boolean
  onClose: () => void
}

const DemoTutorial: React.FC<DemoTutorialProps> = ({ isVisible = true, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0)

  const steps = [
    {
      title: "Welcome to the USP Demo",
      icon: Store,
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-100 dark:bg-pink-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store className="w-8 h-8 text-pink-600 dark:text-pink-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Flower Store Simulation
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              This is a realistic simulation of an e-commerce flower store showcasing MomentScience's User Selected Perks (USP).
            </p>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">How USP Works</h4>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">1</div>
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-1">Perks are Displayed</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Users are presented with personalized offers like shipping discounts, gifts, and exclusive perks.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">2</div>
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-1">User Selects Perks</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Users actively choose which perks they want - each selection is tracked via API in real-time.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">3</div>
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-1">Checkout & Delivery</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      After checkout, MomentScience sends selected perks via webhook to be included in the receipt email.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "API Explorer & Monitoring",
      icon: Code,
      content: (
        <div className="space-y-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Code className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Real-time API Monitoring
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Monitor every API call and response in real-time as you interact with the demo.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
              <div className="flex items-center space-x-2 mb-2">
                <Eye className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h4 className="font-medium text-purple-900 dark:text-purple-100">How to Monitor APIs</h4>
              </div>
              <p className="text-sm text-purple-800 dark:text-purple-200 mb-3">
                Click the <strong>"API"</strong> button in the header (top right) to open a sidebar that shows all requests and responses in action.
              </p>
              <div className="bg-purple-100 dark:bg-purple-800/20 rounded-lg p-3">
                <p className="text-xs text-purple-700 dark:text-purple-300">
                  💡 <strong>Tip:</strong> Keep the API sidebar open while selecting perks to see live API calls!
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                <h5 className="font-medium text-blue-900 dark:text-blue-100 text-sm mb-1">Session API</h5>
                <p className="text-xs text-blue-800 dark:text-blue-200">Monitor session management, offer selection, and wrap API calls</p>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                <h5 className="font-medium text-green-900 dark:text-green-100 text-sm mb-1">Moments API</h5>
                <p className="text-xs text-green-800 dark:text-green-200">Track offer fetching and MomentScience API interactions</p>
              </div>
              
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                <h5 className="font-medium text-orange-900 dark:text-orange-100 text-sm mb-1">Selection History</h5>
                <p className="text-xs text-orange-800 dark:text-orange-200">See all user selections and API call details</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center space-x-2 mb-2">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h4 className="font-medium text-yellow-900 dark:text-yellow-100">Webhook Integration</h4>
              </div>
              <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                After completing checkout, we send a webhook with selected perks to your system.
              </p>
              <div className="bg-yellow-100 dark:bg-yellow-800/20 rounded-lg p-3">
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  <strong>Real API Key Users:</strong> You'll receive webhooks to your defined URL if you're using your real API key (not the demo one). Make sure to configure your webhook URL in your MomentScience dashboard first.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Ready to Explore!",
      icon: CreditCard,
      content: (
        <div className="space-y-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-900/20 dark:to-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-pink-600 dark:text-pink-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Start Your Demo Journey
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              You're all set! Here's what to do next:
            </p>
          </div>
          
          <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-pink-200 dark:border-pink-800">
            <h4 className="font-medium text-pink-900 dark:text-pink-100 mb-3">Quick Start Guide:</h4>
            <ol className="text-sm text-pink-800 dark:text-pink-200 space-y-2">
              <li className="flex items-start space-x-2">
                <span className="bg-pink-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">1</span>
                <span>Select the perks you want to claim from the "Choose Your Perks" section</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="bg-pink-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">2</span>
                <span>Open the API Monitor (top right) to see real-time API calls</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="bg-pink-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">3</span>
                <span>Complete the checkout process to see the wrap API call</span>
              </li>
            </ol>
          </div>
          
          <div className="text-center">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg font-medium hover:from-pink-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Let's Get Started!
            </button>
          </div>
        </div>
      )
    }
  ]

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const goToStep = (step: number) => {
    setCurrentStep(step)
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
                  {React.createElement(steps[currentStep].icon, { className: "w-5 h-5 text-white" })}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {steps[currentStep].title}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Step {currentStep + 1} of {steps.length}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToStep(index)}
                    className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                      index <= currentStep
                        ? 'bg-gradient-to-r from-pink-500 to-purple-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {steps[currentStep].content}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  currentStep === 0
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>

              <div className="flex items-center space-x-2">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToStep(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      index === currentStep
                        ? 'bg-pink-500 w-4'
                        : index < currentStep
                        ? 'bg-pink-300 dark:bg-pink-700'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>

              {currentStep < steps.length - 1 ? (
                <button
                  onClick={nextStep}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:from-pink-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={onClose}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:from-pink-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span>Start Demo</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default DemoTutorial
